import { Link } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { brandRewardsRedemptionsPath } from "@/routes"

/**
 * OKS-61 — Filter pills (segmented control) for the brand-side
 * Redemptions queue at `/brand/rewards/redemptions`.
 *
 * UX Spec §3:
 *   - 4 pills: All / Pending / Fulfilled / Cancelled.
 *   - Active pill: filled `variant="default"`. Inactive: `ghost`.
 *   - Per-pill count chip (server-provided in props).
 *   - Click = Inertia navigation (`<Link>`); URL is the source of
 *     truth for filter state (UX-OQ-11). NO local React state.
 *
 * Accessibility (UX Spec §8):
 *   - `role="tablist"` on the wrapper.
 *   - Each pill: `role="tab"`, `aria-selected` reflecting active.
 *   - Count chips: `aria-hidden` (the link's full label already
 *     carries enough context for screen readers).
 */
type StatusFilter = "all" | "pending" | "shipped" | "fulfilled" | "cancelled"

interface RedemptionFilterPillsProps {
  activeStatus: StatusFilter
  // OKS-563 — a TOTAL Record rather than a hand-listed object. A server
  // response missing a status key is now a type error here instead of a
  // blank count chip at runtime.
  counts: Record<StatusFilter, number>
}

// OKS-563 — five pills. Order is LIFECYCLE order after `all`
// (pending → shipped → fulfilled → cancelled), so the strip reads as the
// path a redemption actually takes rather than as an arbitrary list.
const PILLS: readonly StatusFilter[] = [
  "all",
  "pending",
  "shipped",
  "fulfilled",
  "cancelled",
] as const

export function RedemptionFilterPills({
  activeStatus,
  counts,
}: RedemptionFilterPillsProps) {
  const { t } = useTranslation()

  return (
    <div
      role="tablist"
      className="bg-muted inline-flex gap-1 rounded-lg p-1"
      data-testid="redemptions-filter-pills"
    >
      {PILLS.map((status) => {
        const isActive = activeStatus === status
        const count = counts[status]
        return (
          <Button
            key={status}
            asChild
            size="sm"
            variant={isActive ? "default" : "ghost"}
            className={cn(isActive ? undefined : "hover:bg-background")}
          >
            <Link
              role="tab"
              aria-selected={isActive}
              href={brandRewardsRedemptionsPath({ status })}
              preserveScroll
              data-testid={`redemptions-filter-${status}`}
              data-active={isActive ? "true" : "false"}
            >
              {t(`rewards.redemptions.filter.${status}`)}
              <Badge
                variant="secondary"
                className="ml-2"
                aria-hidden="true"
                data-testid={`redemptions-filter-${status}-count`}
              >
                {count}
              </Badge>
            </Link>
          </Button>
        )
      })}
    </div>
  )
}

export type { StatusFilter }
